import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BellPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatINR } from "@/lib/format";

export const SavedSearchButton = ({ filters, onAuth }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [existing, setExisting] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.get("/saved-searches").then(({ data }) => setExisting(data)).catch(() => {});
  }, [user, open]);

  const save = async (e) => {
    e.preventDefault();
    const clean = Object.fromEntries(Object.entries(filters).filter(([k, v]) => v !== "" && v != null && k !== "q"));
    const payload = { name: name || `Search · ${filters.locality || "All Mysuru"}`, ...clean };
    if (maxPrice) payload.max_price = Number(maxPrice);
    try {
      await api.post("/saved-searches", payload);
      toast.success("Saved! You'll be notified when a new match is approved.");
      setName(""); setMaxPrice("");
      setOpen(false);
    } catch {
      toast.error("Failed to save");
    }
  };

  const remove = async (id) => {
    await api.delete(`/saved-searches/${id}`);
    setExisting((e) => e.filter((s) => s.id !== id));
    toast.success("Removed");
  };

  if (!user) {
    return (
      <button onClick={() => onAuth?.("login")} className="btn-ghost" data-testid="save-search-signin">
        <BellPlus className="w-4 h-4" /> Save Search
      </button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn-ghost" data-testid="save-search-btn">
          <BellPlus className="w-4 h-4" /> Save Search
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="save-search-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Save this search</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div>
            <Label>Name this search</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. 3BHK Gokulam under 1Cr" data-testid="save-search-name" />
          </div>
          <div>
            <Label>Max price (₹) — optional</Label>
            <Input type="number" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="e.g. 15000000" data-testid="save-search-max-price" />
          </div>
          <div className="text-xs text-[var(--ink-muted)]">
            Filters captured: {Object.entries(filters).filter(([k, v]) => v && k !== "q").map(([k, v]) => `${k}=${v}`).join(" · ") || "any"}
          </div>
          <button type="submit" className="btn-primary w-full" data-testid="save-search-submit">Save &amp; Notify Me</button>
        </form>

        {existing.length > 0 && (
          <div className="mt-5 pt-5 border-t border-[var(--line)]">
            <div className="text-xs uppercase tracking-wider font-semibold text-[var(--ink-muted)] mb-2">Your saved searches</div>
            <div className="space-y-2">
              {existing.map((s) => (
                <div key={s.id} className="flex items-center justify-between p-2 rounded-lg bg-[var(--surface)] text-sm" data-testid={`saved-search-${s.id}`}>
                  <div>
                    <div className="font-medium text-[var(--teak)]">{s.name}</div>
                    <div className="text-xs text-[var(--ink-muted)]">
                      {[s.locality, s.listing_type, s.bhk && `${s.bhk} BHK`, s.max_price && `≤ ${formatINR(s.max_price, s.listing_type)}`].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <button onClick={() => remove(s.id)} className="p-1.5 rounded hover:bg-white" data-testid={`delete-search-${s.id}`}>
                    <Trash2 className="w-3.5 h-3.5 text-[var(--ink-muted)]" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
